import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../../utils/config';
import '../../assets/css/Rejoindre/Rejoindre_Form.css';

function JoinForm() {
    const [step, setStep] = useState(1);
    const [antennas, setAntennas] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        antenne_id: '',
        civilite: '',
        nom: '',
        prenom: '',
        telephone: '',
        email: '',
        adresse: '',
        ville: '',
        code_postal: '',
        profession: '',
        qualification: '',
        motivation: '',
        rgpd_validation: false,
    });

    const [message, setMessage] = useState(null);
    const formRef = useRef(null);

    useEffect(() => {
        fetch(`${API_BASE_URL}/antennes/`)
            .then((response) => response.json())
            .then((data) => setAntennas(data))
            .catch((error) => console.error('Erreur lors de la récupération des antennes:', error));
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const scrollToForm = () => {
        if (formRef.current) {
            formRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    };

    const handleNextStep = (e) => {
        e.preventDefault();
        setStep(step + 1);
        scrollToForm();
    };

    const handlePreviousStep = () => {
        setStep(step - 1);
        scrollToForm();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        fetch(`${API_BASE_URL}/join/submit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData),
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.error) {
                    setMessage({ type: 'error', text: data.error });
                } else {
                    setMessage({ type: 'success', text: data.message });
                }
            })
            .catch(() => setMessage({ type: 'error', text: 'Une erreur est survenue.' }))
            .finally(() => setIsSubmitting(false)); 
    };

    const resetForm = () => {
        setFormData({
            antenne_id: '',
            civilite: '',
            nom: '',
            prenom: '',
            telephone: '',
            email: '',
            adresse: '',
            ville: '',
            code_postal: '',
            profession: '',
            qualification: '',
            motivation: '',
            rgpd_validation: false,
        });
        setStep(1);
        setMessage(null);
    };

    return (
        <div ref={formRef} className="join-form">
            <h2>Nous Rejoindre</h2>

            {/* Message de succès ou d'erreur */}
            {message ? (
                <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
                    <p>{message.text}</p>
                    {message.type === 'success' && (
                        <button onClick={resetForm} className="restart-button">
                            Recommencer
                        </button>
                    )}
                </div>
            ) : (
                <>
                    {/* Progress Bar */}
                    <div className="progress-bar-container">
                        <div className="progress-bar">
                            <div
                                className="progress-bar-step"
                                style={{ width: `${(step / 3) * 100}%` }}
                            ></div>
                        </div>
                    </div>

                    {/* Step 1 */}
                    {step === 1 && (
                        <form onSubmit={handleNextStep}>
                            <div className="form-group">
                                <label>Antenne:</label>
                                <select name="antenne_id" value={formData.antenne_id} onChange={handleChange} required>
                                    <option value="">Choisir une antenne</option>
                                    {antennas.map((antenna) => (
                                        <option key={antenna.id} value={antenna.id}>
                                            {antenna.nom}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Civilité:</label>
                                <select name="civilite" value={formData.civilite} onChange={handleChange} required>
                                    <option value="">Choisir</option>
                                    <option value="M">Monsieur</option>
                                    <option value="Mme">Madame</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Nom:</label>
                                <input type="text" name="nom" value={formData.nom} onChange={handleChange} required />
                            </div>
                            <div className="form-group">
                                <label>Prénom:</label>
                                <input type="text" name="prenom" value={formData.prenom} onChange={handleChange} required />
                            </div>
                            <div className="form-group">
                                <label>Téléphone:</label>
                                <input type="text" name="telephone" value={formData.telephone} onChange={handleChange} required />
                            </div>
                            <div className="form-group">
                                <label>Email:</label>
                                <input type="email" name="email" value={formData.email} onChange={handleChange} required />
                            </div>
                            <div className="join-form-buttons">
                                <button type="submit">Suivant</button>
                            </div>
                        </form>
                    )}

                    {/* Step 2 */}
                    {step === 2 && (
                        <form onSubmit={handleNextStep}>
                            <div className="form-group">
                                <label>Adresse Postale:</label>
                                <input type="text" name="adresse" value={formData.adresse} onChange={handleChange} required />
                            </div>
                            <div className="form-group">
                                <label>Ville:</label>
                                <input type="text" name="ville" value={formData.ville} onChange={handleChange} required />
                            </div>
                            <div className="form-group">
                                <label>Code Postal:</label>
                                <input type="text" name="code_postal" value={formData.code_postal} onChange={handleChange} required />
                            </div>
                            <div className="join-form-buttons">
                                <button type="button" onClick={handlePreviousStep}>
                                    Retour
                                </button>
                                <button type="submit">Suivant</button>
                            </div>
                        </form>
                    )}

                    {/* Step 3 */}
                    {step === 3 && (
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Profession:</label>
                                <input type="text" name="profession" value={formData.profession} onChange={handleChange} />
                            </div>
                            <div className="form-group">
                                <label>Qualification en secourisme:</label>
                                <select name="qualification" value={formData.qualification} onChange={handleChange}>
                                    <option value="Aucune">Aucune</option>
                                    <option value="PSC1">PSC1</option>
                                    <option value="PSE1">PSE1</option>
                                    <option value="PSE2">PSE2</option>
                                    <option value="AFGSU">AFGSU</option>
                                    <option value="SUAP">SUAP</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Raisons de votre engagement:</label>
                                <textarea
                                    name="motivation"
                                    value={formData.motivation}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="checkbox-group">
                                <input
                                    type="checkbox"
                                    name="rgpd_validation"
                                    checked={formData.rgpd_validation}
                                    onChange={handleChange}
                                    required
                                />
                                <label>
                                    J'accepte que les informations saisies soient stockées pour permettre de me recontacter dans le cadre de ma demande.
                                </label>
                            </div>
                            <div className="join-form-buttons">
                                <button type="button" onClick={handlePreviousStep}>
                                    Retour
                                </button>
                                <button type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        </>
                                    ) : (
                                        "Envoyer"
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </>
            )}
        </div>
    );
}

export default JoinForm;
